import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';

const AddSessionModal = ({ game, onClose, onSave }) => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [sessionStats, setSessionStats] = useState(null);
  const [quickRating, setQuickRating] = useState(null);
  const [savingRating, setSavingRating] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/api/games/${game.id}/sessions`, { played_at: new Date(date).toISOString(), duration_minutes: duration ? parseInt(duration) : null, notes: notes || null });
      // Load session stats for recap
      try {
        const sessions = await api.get(`/api/games/${game.id}/sessions`);
        const totalMinutes = sessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
        const myUserData = game.user_data?.[localStorage.getItem('lastUsername')] || {};
        setSessionStats({
          sessionCount: sessions.length,
          totalMinutes,
          currentRating: myUserData.rating,
          hasNotes: !!myUserData.notes
        });
      } catch {}
      setShowRecap(true);
      onSave();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleQuickRate = async (rating) => {
    setQuickRating(rating);
    setSavingRating(true);
    try {
      await api.put(`/api/games/${game.id}/rating`, { rating, notes: null });
      setSessionStats(prev => ({ ...prev, currentRating: rating }));
    } catch (err) { console.error(err); }
    finally { setSavingRating(false); }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0h';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const inputStyle = { width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '14px', color: THEME.text, fontSize: '14px', boxSizing: 'border-box', outline: 'none' };

  // Session Recap Screen
  if (showRecap) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', duration: 0.4 }}
          style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', padding: '28px', width: '90%', maxWidth: '420px', border: `1px solid ${THEME.success}40`, boxShadow: THEME.modalShadow }}
          onClick={e => e.stopPropagation()}
        >
          {/* Success header */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.1 }}
              style={{ width: '64px', height: '64px', background: `${THEME.success}20`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '32px' }}
            >
              ✓
            </motion.div>
            <h3 style={{ margin: '0 0 8px', color: THEME.text, fontSize: '20px', fontWeight: '700' }}>Session Logged!</h3>
            <p style={{ margin: 0, color: THEME.textMuted, fontSize: '14px' }}>{game.name}</p>
          </div>

          {/* Session summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
            <div style={{ background: THEME.bg, borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: THEME.success }}>{formatDuration(parseInt(duration) || 0)}</div>
              <div style={{ fontSize: '11px', color: THEME.textMuted, textTransform: 'uppercase' }}>This Session</div>
            </div>
            <div style={{ background: THEME.bg, borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: THEME.info }}>{sessionStats?.sessionCount || 1}</div>
              <div style={{ fontSize: '11px', color: THEME.textMuted, textTransform: 'uppercase' }}>Total Sessions</div>
            </div>
          </div>

          {/* Total playtime bar */}
          <div style={{ background: THEME.bg, borderRadius: '12px', padding: '14px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: THEME.textMuted, fontSize: '12px', textTransform: 'uppercase' }}>Total Logged Playtime</span>
              <span style={{ color: THEME.accent, fontWeight: '700', fontSize: '16px' }}>{formatDuration(sessionStats?.totalMinutes || parseInt(duration) || 0)}</span>
            </div>
          </div>

          {/* Quick rating (if not rated) */}
          {sessionStats && !sessionStats.currentRating && (
            <div style={{ background: `${THEME.warning}10`, borderRadius: '12px', padding: '16px', marginBottom: '20px', border: `1px solid ${THEME.warning}30` }}>
              <div style={{ color: THEME.warning, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '10px' }}>Quick Rate This Game</div>
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(r => (
                  <button
                    key={r}
                    onClick={() => handleQuickRate(r)}
                    disabled={savingRating}
                    style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      background: quickRating === r ? THEME.warning : THEME.bg,
                      color: quickRating === r ? '#000' : THEME.text,
                      border: `1px solid ${quickRating === r ? THEME.warning : THEME.border}`,
                      cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                      transition: 'all 0.15s'
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Rating display (if already rated) */}
          {sessionStats?.currentRating && (
            <div style={{ background: `${THEME.success}10`, borderRadius: '12px', padding: '14px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span style={{ color: THEME.textMuted, fontSize: '13px' }}>Your Rating:</span>
              <span style={{ color: THEME.success, fontWeight: '700', fontSize: '18px' }}>{sessionStats.currentRating}/10</span>
              <span style={{ fontSize: '16px' }}>{sessionStats.currentRating >= 8 ? '⭐' : sessionStats.currentRating >= 5 ? '👍' : '👎'}</span>
            </div>
          )}

          {/* Close button */}
          <button onClick={onClose} style={{ width: '100%', background: THEME.success, color: '#000', border: 'none', borderRadius: '10px', padding: '14px', fontWeight: '600', cursor: 'pointer', fontSize: '15px' }}>
            Done
          </button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }} onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }} style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', padding: '28px', width: '90%', maxWidth: '420px', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 24px', color: THEME.text, fontSize: '18px', fontWeight: '700' }}>Log Session: {game.name}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', marginBottom: '24px' }}>
          <div>
            <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date Played</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Duration (minutes)</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              {[{ label: '30m', value: 30 }, { label: '1h', value: 60 }, { label: '2h', value: 120 }, { label: '3h', value: 180 }, { label: '4h', value: 240 }].map(preset => (
                <button key={preset.value} onClick={() => setDuration(preset.value.toString())} style={{ flex: 1, background: parseInt(duration) === preset.value ? THEME.success : THEME.bg, color: parseInt(duration) === preset.value ? '#000' : THEME.text, border: `1px solid ${parseInt(duration) === preset.value ? THEME.success : THEME.border}`, borderRadius: '8px', padding: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}>{preset.label}</button>
              ))}
            </div>
            <input type="number" value={duration} onChange={e => setDuration(e.target.value)} placeholder="Or enter custom..." style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="How was the session?" style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, background: THEME.success, color: '#000', border: 'none', borderRadius: '10px', padding: '14px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '15px' }}>{saving ? 'Saving...' : 'Log Session'}</button>
          <button onClick={onClose} style={{ background: THEME.bgHover, color: THEME.textMuted, border: 'none', borderRadius: '10px', padding: '14px 28px', cursor: 'pointer', fontSize: '15px' }}>Cancel</button>
        </div>
      </motion.div>
    </div>
  );
};

export default AddSessionModal;
